import React, { memo, useEffect, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import useKeyboardShortcuts, { useKeyboardState } from "../../contexts/KeyboardShortcutContext";
import useRouting, { useParams } from "../../contexts/Routing";
import { DashboardTicketDisplay, useGetTicketQuery } from "../../__generated__/api";
import Styles from "./TicketView.module.scss";
import { useTicketState } from "../../contexts/ticket-state";
import ChatView from "../agent-v3/chat/ChatView";
import { ChatError } from "../agent-v3/chat/Chat";
import { LoggingHandler } from "../../contexts/logging/LoggingContext";

const KEYBOARD_CONTEXT = "tickets";

export type Ticket = Exclude<ReturnType<(typeof useGetTicketQuery)>["data"], undefined>["ticket"];

function TicketView() {
    const { Paths, historyPushWithSearch, getSearchParams } = useRouting();
    const { ticketId } = useParams(Paths.TICKETS_VIEW);

    const ticketState = useTicketState();
    const { ticket, error } = ticketState.tickets.useGetOrFetch(ticketId);

    const [isShow, setIsShow] = useState(false);

    const keyboardState = useKeyboardState();

    useEffect(() => {
        if (ticketId) {
            setIsShow(true);
            keyboardState.pushContext(KEYBOARD_CONTEXT);
        } else {
            setIsShow(false);
            keyboardState.popContext(KEYBOARD_CONTEXT);
        }
    }, [ticketId]);

    const hide = () => {
        setIsShow(false);
        historyPushWithSearch(Paths.TICKETS, getSearchParams(Paths.TICKETS));
    };

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            shortcut: "esc",
            shouldUseAllContexts: true,
            context: KEYBOARD_CONTEXT,
            handler: () => {
                hide();
            },
        });
    });

    const chatDisplay = error ? (
        <ChatError />
    ) : (
        <ChatView
            ticket={ticket}
            onClose={hide}
            key={ticket?.id}
            metricPathSection={LoggingHandler.Metrics.TICKETS}
            isViewPrivate
            displayType={DashboardTicketDisplay.SideRight}
            onToggleDisplay={() => {
                ticketState.currentDashboardSettings = {
                    ...ticketState.currentDashboardSettings,
                    ticketDisplay: DashboardTicketDisplay.Modal,
                };
            }}
            isReadOnly
        />
    );

    return (
        <Offcanvas
            show={isShow}
            className={Styles["chat-offcanvas"]}
            scroll
            backdrop={false}
            onHide={hide}
            placement="end"
            keyboard={false}
        >
            <Offcanvas.Body>
                {chatDisplay}
            </Offcanvas.Body>
        </Offcanvas>
    );
}

export default memo(TicketView);
